import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Network, Building2, MapPin, Plus, UserPlus, X } from 'lucide-react';
import { orgs, users } from '../api/client';
import { MetricCard } from '../components/MetricCard';
import type { Organisation } from '../types';

// ── Shared field/button primitives ───────────────────────────────────

const inputCls =
  'w-full bg-surface-2 border border-surface-3 rounded-md px-2.5 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500';
const selectCls =
  'w-full bg-surface-2 border border-surface-3 rounded-md px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500';
const btnPrimary =
  'px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

// ── Tier options ─────────────────────────────────────────────────────

const TIERS = ['base', 'pro', 'enterprise'] as const;

// ── Add-Integrator inline form ────────────────────────────────────────

function AddIntegratorForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [orgName, setOrgName] = useState('');
  const [region, setRegion] = useState('NZ');
  const [tier, setTier] = useState<string>('base');

  const mutation = useMutation({
    mutationFn: () =>
      orgs.create({ org_name: orgName.trim(), org_type: 'integrator', tier }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      onClose();
    },
  });

  return (
    <form
      className="mt-3 p-3 bg-surface-2 rounded-lg border border-surface-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          New Integrator
        </span>
        <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-300">
          <X size={13} />
        </button>
      </div>
      <input
        className={inputCls}
        placeholder="Organisation name"
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
        required
      />
      <div className="flex gap-2">
        <input
          className={inputCls}
          placeholder="Region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          required
        />
        <select className={selectCls} value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIERS.map((t) => (
            <option key={t} value={t} className="capitalize">
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </div>
      {mutation.isError && (
        <p className="text-[10px] text-red-400">
          {(mutation.error as Error).message}
        </p>
      )}
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create Integrator'}
        </button>
      </div>
    </form>
  );
}

// ── Add-Client inline form ────────────────────────────────────────────

function AddClientForm({
  integrator,
  onClose,
}: {
  integrator: Organisation;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [orgName, setOrgName] = useState('');
  const [tier, setTier] = useState<string>('base');

  const mutation = useMutation({
    mutationFn: () =>
      orgs.create({
        org_name: orgName.trim(),
        org_type: 'client',
        tier,
        parent_org_id: integrator.org_id,
        region: integrator.region,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      onClose();
    },
  });

  return (
    <form
      className="mt-2 p-3 bg-surface-3/40 rounded-lg border border-surface-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          New Client for {integrator.org_name}
        </span>
        <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-300">
          <X size={13} />
        </button>
      </div>
      <input
        className={inputCls}
        placeholder="Client organisation name"
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
        required
      />
      <select className={selectCls} value={tier} onChange={(e) => setTier(e.target.value)}>
        {TIERS.map((t) => (
          <option key={t} value={t}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </option>
        ))}
      </select>
      {mutation.isError && (
        <p className="text-[10px] text-red-400">
          {(mutation.error as Error).message}
        </p>
      )}
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Add Client'}
        </button>
      </div>
    </form>
  );
}

// ── Invite-Admin inline form ──────────────────────────────────────────

function InviteAdminForm({
  target,
  onClose,
}: {
  target: Organisation;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      users.invite(email.trim(), 'admin', {
        name: name.trim() || undefined,
        org_id: target.org_id,
        org_type: target.org_type,
      }),
    onSuccess: () => {
      onClose();
    },
  });

  return (
    <form
      className="mt-2 p-3 bg-surface-3/40 rounded-lg border border-surface-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          Invite Admin — {target.org_name}
        </span>
        <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-300">
          <X size={13} />
        </button>
      </div>
      <input
        className={inputCls}
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <input
        className={inputCls}
        placeholder="Full name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {mutation.isError && (
        <p className="text-[10px] text-red-400">
          {(mutation.error as Error).message}
        </p>
      )}
      {mutation.isSuccess && (
        <p className="text-[10px] text-green-400">Invite sent.</p>
      )}
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={mutation.isPending}>
          {mutation.isPending ? 'Sending…' : 'Send Invite'}
        </button>
      </div>
    </form>
  );
}

// ── Client card row ───────────────────────────────────────────────────

function ClientRow({ client }: { client: Organisation }) {
  const [showInvite, setShowInvite] = useState(false);

  return (
    <div className="bg-surface-2 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-300">{client.org_name}</div>
          <div className="text-[10px] text-gray-500 capitalize">
            {client.tier} &middot; {client.region}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs font-mono text-gray-400">{client.device_serials.length} devices</div>
          <button
            type="button"
            onClick={() => setShowInvite((v) => !v)}
            className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-blue-400 transition-colors"
            title="Invite admin"
          >
            <UserPlus size={11} />
            <span>Invite</span>
          </button>
        </div>
      </div>
      {showInvite && (
        <InviteAdminForm target={client} onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
}

// ── Integrator card ───────────────────────────────────────────────────

function IntegratorCard({
  integrator,
  intClients,
}: {
  integrator: Organisation;
  intClients: Organisation[];
}) {
  const intDevices = intClients.reduce((sum, c) => sum + c.device_serials.length, 0);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);

  return (
    <div className="card">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500/15 rounded-lg flex items-center justify-center">
            <Building2 size={18} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{integrator.org_name}</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin size={10} /> {integrator.region}
              <span className="capitalize">&middot; {integrator.tier} tier</span>
            </div>
          </div>
        </div>

        {/* Right-side stats + action buttons */}
        <div className="flex items-center gap-3">
          <div className="text-right text-xs text-gray-500">
            <div>{intClients.length} clients</div>
            <div>{intDevices} devices</div>
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => {
                setShowAddClient((v) => !v);
                setShowInviteAdmin(false);
              }}
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-blue-400 transition-colors whitespace-nowrap"
            >
              <Plus size={11} />
              <span>Add Client</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setShowInviteAdmin((v) => !v);
                setShowAddClient(false);
              }}
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-blue-400 transition-colors whitespace-nowrap"
            >
              <UserPlus size={11} />
              <span>Invite Admin</span>
            </button>
          </div>
        </div>
      </div>

      {/* Inline Add Client form */}
      {showAddClient && (
        <AddClientForm integrator={integrator} onClose={() => setShowAddClient(false)} />
      )}

      {/* Inline Invite Admin form */}
      {showInviteAdmin && (
        <InviteAdminForm target={integrator} onClose={() => setShowInviteAdmin(false)} />
      )}

      {/* Client deployments */}
      {intClients.length > 0 && (
        <div className="border-t border-surface-3 pt-3 mt-3">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Client Deployments
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {intClients.map((client) => (
              <ClientRow key={client.org_id} client={client} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

/**
 * Kinetica-only page: view the integrator network and their client deployments.
 */
export default function IntegratorsPage() {
  const { data: organisations = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: orgs.list,
  });

  const [showAddIntegrator, setShowAddIntegrator] = useState(false);

  const integrators = organisations.filter((o: Organisation) => o.org_type === 'integrator');
  const clients = organisations.filter((o: Organisation) => o.org_type === 'client');
  const totalDevices = organisations.reduce(
    (sum: number, o: Organisation) => sum + o.device_serials.length,
    0,
  );

  return (
    <div>
      {/* Page heading + Add Integrator */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Integrator Network</h2>
        <button
          type="button"
          onClick={() => setShowAddIntegrator((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-md transition-colors"
        >
          <Plus size={13} />
          Add Integrator
        </button>
      </div>

      {/* Inline Add Integrator form */}
      {showAddIntegrator && (
        <AddIntegratorForm onClose={() => setShowAddIntegrator(false)} />
      )}

      {/* Summary metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 mt-4">
        <MetricCard label="Integrators" value={integrators.length} />
        <MetricCard label="Clients" value={clients.length} />
        <MetricCard label="Total Devices" value={totalDevices} />
        <MetricCard
          label="Regions"
          value={new Set(organisations.map((o: Organisation) => o.region)).size}
        />
      </div>

      {/* Integrator cards */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="card text-center text-gray-500 text-sm py-8">Loading network…</div>
        ) : integrators.length === 0 ? (
          <div className="card text-center py-8">
            <Network className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No integrators registered</p>
          </div>
        ) : (
          integrators.map((integrator: Organisation) => {
            const intClients = clients.filter(
              (c: Organisation) => c.parent_org_id === integrator.org_id,
            );
            return (
              <IntegratorCard
                key={integrator.org_id}
                integrator={integrator}
                intClients={intClients}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
